import { GuestPage } from '@/features/sharing/GuestPage'
import { safeShareReturn } from '@/features/sharing/types'
import { Music, WifiOff } from 'lucide-react'
import type { ReactNode } from 'react'
import { AlbumPage } from '@/features/albums/AlbumPage'
import { Navigate, Route, Routes, useLocation } from 'react-router'

import { useMe } from '@/api/hooks'
import { AppShell } from '@/components/layout/AppShell'
import { Button, Spinner } from '@/components/ui'
import { LoginPage } from '@/features/auth/LoginPage'
import { RegisterPage } from '@/features/auth/RegisterPage'
import { ArtistPage } from '@/features/artists/ArtistPage'
import { SimilarPage } from '@/features/discover/SimilarPage'
import { DownloadsPage } from '@/features/downloads/DownloadsPage'
import { LibraryPage } from '@/features/library/LibraryPage'
import { HomePage } from '@/features/home/HomePage'
import { DaylistPage } from '@/features/daylists/DaylistPage'
import { NotFoundPage } from '@/features/misc/NotFoundPage'
import { PlaylistDetailPage } from '@/features/playlists/PlaylistDetailPage'
import { SearchPage } from '@/features/search/SearchPage'
import { HistoryPage } from '@/features/settings/HistoryPage'
import { SettingsPage } from '@/features/settings/SettingsPage'

/**
 * Shown while the session check is in flight. Branded rather than a bare
 * spinner: this is the first thing on screen on a cold load, and it has to look
 * like the app booting, not like something hanging.
 */
function Splash() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-surface-sunken px-6">
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent">
          <Music className="h-6 w-6 text-on-accent" aria-hidden="true" />
        </span>
        <span className="text-2xl font-semibold tracking-tight text-text">Tunes</span>
      </div>
      <Spinner size="sm" className="text-text-subtle" label="Signing you in" />
    </div>
  )
}

function ConnectionError({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-surface-sunken px-6 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-hover">
        <WifiOff className="h-6 w-6 text-text-subtle" aria-hidden="true" />
      </span>
      <div className="max-w-sm space-y-1">
        <p className="text-base font-medium text-text">Can&rsquo;t reach the server</p>
        <p className="text-sm text-text-muted">
          Tunes could not check your session. Check your connection and try again.
        </p>
      </div>
      <Button onClick={onRetry} loading={retrying}>
        Try again
      </Button>
    </div>
  )
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { data: user, isPending, isError, isFetching, refetch } = useMe()
  const location = useLocation()

  if (isPending) return <Splash />
  if (isError) return <ConnectionError onRetry={() => void refetch()} retrying={isFetching} />
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />

  return <>{children}</>
}

/** Login and register are pointless once there is a session. */
function PublicOnly({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { data: user, isPending } = useMe()

  if (isPending) return <Splash />
  if (user) return <Navigate to={safeShareReturn(location.state?.from)} replace />

  return <>{children}</>
}

export function App() {
  return (
    <Routes>
      <Route path="/share/:id" element={<GuestPage />} />
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnly>
            <RegisterPage />
          </PublicOnly>
        }
      />

      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="daylists/:id" element={<DaylistPage />} />
        <Route path="library" element={<LibraryPage />} />
        <Route path="albums/local/:releaseId" element={<AlbumPage />} />
        <Route path="albums/:provider/:externalId" element={<AlbumPage />} />
        <Route path="library/albums/:albumId" element={<AlbumPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="artists/local/:localArtistId" element={<ArtistPage />} />
        <Route path="artists/:artistId" element={<ArtistPage />} />
        <Route path="artists/provider/:provider/:externalId" element={<ArtistPage />} />
        <Route path="playlists" element={<Navigate to="/library?view=playlists" replace />} />
        <Route path="playlists/:id" element={<PlaylistDetailPage />} />
        <Route path="downloads" element={<DownloadsPage />} />
        <Route path="similar/local/:localArtistId" element={<SimilarPage />} />
        <Route path="similar/:artistId" element={<SimilarPage />} />
        <Route path="similar/provider/:provider/:externalId" element={<SimilarPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/history" element={<HistoryPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}
